import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, Gem } from "lucide-react";
import { toast } from "sonner";
import { Stat } from "@/pages/analytics/TrafficPanel";

const FREQ_COLOR = { "Very Active": "text-[#34D399] border-[#34D399]/40", Active: "text-[#22D3EE] border-[#22D3EE]/40", "Low Activity": "text-primary border-primary/40", Inactive: "text-slate-400 border-white/15" };

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "Never");

export function ExplorerActivityPanel({ data }) {
  const [view, setView] = useState("all");
  const [q, setQ] = useState("");
  const c = data.counts;
  const term = q.trim().toLowerCase();
  const rows = data.explorers.filter((e) => {
    const inView = view === "all" || (view === "active" ? e.frequency !== "Inactive" : view === "trial" ? e.trial : !e.trial);
    const inSearch = !term || [e.name, e.email, ...(e.classes || []), ...(e.guides || [])].some((f) => (f || "").toLowerCase().includes(term));
    return inView && inSearch;
  });

  const copyEmails = () => {
    navigator.clipboard?.writeText(rows.map((e) => e.email).filter(Boolean).join(", ")).catch(() => {});
    toast.success(`Copied ${rows.length} email${rows.length === 1 ? "" : "s"}`);
  };

  return (
    <div className="space-y-6" data-testid="explorer-activity-panel">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Explorers" value={data.total} accent="text-slate-100" testid="stat-explorers-total" />
        <Stat label="Very active" value={c["Very Active"]} accent="text-[#34D399]" />
        <Stat label="Active" value={c.Active} />
        <Stat label="Low activity" value={c["Low Activity"]} accent="text-primary" />
        <Stat label="Inactive (30d)" value={c.Inactive} accent="text-slate-400" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          {[["all", "All"], ["active", "Active only"], ["trial", "Trials"], ["full", "Full accounts"]].map(([id, label]) => (
            <Button key={id} size="sm" data-testid={`explorers-view-${id}`} variant={view === id ? "default" : "outline"} onClick={() => setView(id)}
              className={view === id ? "bg-[#22D3EE] text-[#04121f] hover:bg-[#67E8F9]" : ""}>{label}</Button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Input data-testid="explorer-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, class or Guide" className="bg-white/5 border-white/10 w-64" />
          <Button size="sm" variant="outline" data-testid="copy-explorer-emails-btn" onClick={copyEmails} className="gap-2 shrink-0"><Copy className="w-4 h-4" /> Copy {rows.length} email{rows.length === 1 ? "" : "s"}</Button>
        </div>
      </div>

      <div className="hq-glass rounded-2xl border border-white/10 overflow-auto max-h-[70vh]">
        <table className="w-full text-sm" data-testid="explorers-table">
          <thead className="sticky top-0 bg-[#0b1220] text-[10px] uppercase tracking-widest text-muted-foreground">
            <tr>{["Explorer", "Class · Guide", "Total logins", "Last login", "30-day logins", "Frequency", "Points", "Lessons", "Tests passed", "Joined"].map((h) => <th key={h} className="text-left px-4 py-3 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={10} className="px-4 py-10 text-center text-muted-foreground">No Explorers in this view.</td></tr>}
            {rows.map((e) => (
              <tr key={e.user_id} data-testid={`explorer-row-${e.user_id}`} className="border-t border-white/5 hover:bg-white/[0.03]">
                <td className="px-4 py-3">
                  <p className="font-medium">{e.name || "Unnamed"}{e.trial && <span className="ml-2 text-[10px] text-primary">trial</span>}</p>
                  <p className="text-xs text-muted-foreground">{e.email}</p>
                </td>
                <td className="px-4 py-3">
                  <p className="text-slate-200">{e.classes?.length ? e.classes.join(", ") : <span className="text-muted-foreground">No class yet</span>}</p>
                  {e.guides?.length > 0 && <p className="text-xs text-muted-foreground">{e.guides.join(", ")}</p>}
                </td>
                <td className="px-4 py-3 font-mono-data">{e.total_logins}</td>
                <td className="px-4 py-3 whitespace-nowrap">{fmt(e.last_login)}</td>
                <td className="px-4 py-3 font-mono-data text-[#22D3EE]">{e.recent_login_count}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full border ${FREQ_COLOR[e.frequency]}`}>{e.frequency}</span></td>
                <td className="px-4 py-3 font-mono-data text-primary flex items-center gap-1"><Gem className="w-3.5 h-3.5" />{e.horizon_points ?? 0}</td>
                <td className="px-4 py-3 font-mono-data whitespace-nowrap">{e.missions_mastered}<span className="text-muted-foreground"> / {e.missions_worked}</span></td>
                <td className="px-4 py-3 font-mono-data text-[#34D399]">{e.tests_passed}</td>
                <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmt(e.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">Lessons column shows mastered / worked on. Tests passed counts Checkpoints, Unit Tests and hands-on Task Tests.</p>
    </div>
  );
}
